import { CalendarDays, Mail, TriangleAlert } from "lucide-react";
import { Link } from "react-router";
import { Badge } from "../../primitives/badge";
import type { WorkUnitSummaryItem } from "@shared-core/data/api-types";
import { readinessLabel } from "@shared-core/domain/readiness";
import { plural } from "@shared-core/domain/deadline";
import { formatDateWithYear } from "./BoardSectionShell";
import { currentTime } from "@shared-core/domain/current-time";

/**
 * Compact job card for the alt CSR-home page's WIP-focused board
 * (`CsrHomeAlt.tsx` / `WipFocusedBoard`, csr-dashboard-alt-screen). The
 * original `BoardJobRow` in `PortfolioBoard.tsx` is untouched and keeps
 * powering `/home/csr` unchanged — this is a parallel component, not a
 * modification, so the two pages can be evaluated side by side.
 *
 * Two-line, single-row layout (~46px tall, roughly half the original card's
 * height — "Option A", approved 2026-10-02): a left-edge colour bar
 * (`job.attention` — red/amber/green, computed server-side, see
 * `views_module.api.transformers.csr_home._compute_attention`); a
 * fixed-width status zone (stage/status headline + "since" line, kicker
 * folded into that second line rather than its own row); a flex main zone
 * (job identity + a single meta line that now also carries the open-alert
 * text inline, in place of the old tinted alert row); and a due pill on the
 * right. "Needed by <date>" is dropped from the body — the due pill already
 * conveys it, and the exact date survives as the pill's tooltip
 * (`title`) so it's still discoverable. No "Draft update" button (dropped
 * for this card; the original board keeps it).
 */
export function AltBoardJobRow({ job }: { job: WorkUnitSummaryItem }) {
  const bar = ATTENTION_BAR_CLASS[job.attention];
  const status = cardStatus(job);
  const duePill = dueInfoPill(job);
  const alertTone = job.top_alert_label ? alertLineTone(job) : null;

  return (
    <div className="flex rounded-md border border-border bg-card overflow-hidden text-sm">
      <div className={`w-1 shrink-0 ${bar}`} aria-hidden="true" />
      <div className="flex flex-1 min-w-0 items-center py-1.5 px-2.5 gap-2.5">
        {/* Status zone — fixed width, always in the same place regardless
            of readiness/production state. */}
        <div className="w-[110px] sm:w-[150px] shrink-0 min-w-0 flex flex-col justify-center">
          <span className="font-medium leading-tight truncate">
            {status.headline}
          </span>
          <span className="text-xs text-muted-foreground truncate">
            {status.subline}
          </span>
        </div>

        <Link
          to={job.self_url}
          className="flex-1 min-w-0 flex flex-col justify-center"
        >
          <span className="font-medium truncate">
            <span className="tabular-nums text-muted-foreground">
              #{job.job_no}
            </span>{" "}
            {job.customer_name} — {job.job_title}
            {job.booked_by_you && (
              <Badge
                variant="neutral"
                className="ml-1.5 h-4 px-1.5 py-0 text-[10px] align-middle"
              >
                Booked in by you
              </Badge>
            )}
          </span>

          <span className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
            {job.quantity != null && (
              <span className="tabular-nums shrink-0">
                Qty {job.quantity.toLocaleString()}
              </span>
            )}
            {(job.email_count > 0 || job.unread_email_count > 0) && (
              <span className="inline-flex items-center gap-1 shrink-0">
                <Mail className="size-3" />
                {job.email_count}
                {job.unread_email_count > 0 && (
                  <span className="text-primary font-medium">
                    · {job.unread_email_count} unread
                  </span>
                )}
              </span>
            )}
            {job.meeting_count > 0 && (
              <span className="inline-flex items-center gap-1 shrink-0">
                <CalendarDays className="size-3" />
                {job.meeting_count} mtg
              </span>
            )}
            {job.top_alert_label && alertTone && (
              <span
                className={`inline-flex items-center gap-1 min-w-0 truncate ${ALERT_TEXT_CLASS[alertTone]}`}
              >
                <TriangleAlert className="size-3 shrink-0" />
                <span className="font-medium truncate">
                  {job.top_alert_label}
                </span>
                {job.open_alert_count > 1 && (
                  <span className="shrink-0">+{job.open_alert_count - 1}</span>
                )}
              </span>
            )}
          </span>
        </Link>

        <div className="shrink-0 self-center">
          {duePill && (
            <Badge
              variant={DUE_PILL_VARIANT[duePill.tone]}
              title={
                job.required_at
                  ? formatDateWithYear(job.required_at)
                  : undefined
              }
            >
              {duePill.label}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

const ATTENTION_BAR_CLASS: Record<WorkUnitSummaryItem["attention"], string> = {
  red: "bg-red-500",
  amber: "bg-amber-500",
  green: "bg-green-500",
};

/** The alert text's colour follows the job's worst open alert severity
 *  (`rag`), not `attention` — a finished job can carry a surviving alert
 *  (`attention` stays green by construction, see `_compute_attention`) and
 *  the text must still read red/amber for it, never neutral. */
function alertLineTone(job: WorkUnitSummaryItem): "red" | "amber" {
  return job.rag === "red" ? "red" : "amber";
}

const ALERT_TEXT_CLASS: Record<"red" | "amber", string> = {
  red: "text-red-700 dark:text-red-300",
  amber: "text-amber-700 dark:text-amber-300",
};

const DUE_PILL_VARIANT: Record<
  "red" | "amber" | "neutral",
  "error" | "warning" | "neutral"
> = {
  red: "error",
  amber: "warning",
  neutral: "neutral",
};

/** Readiness values that still precede a press — see
 *  `_compute_attention`'s identical set on the backend (kept independent:
 *  this one drives display wording, not the attention colour). */
const NOT_YET_IN_PRODUCTION: ReadonlySet<WorkUnitSummaryItem["readiness"]> =
  new Set(["not_production_ready", "out_for_proof", "production_ready"]);

/** The status zone's two lines, derived from the job's readiness axis
 *  (never from `rag`/`attention` — readiness and urgency are independent,
 *  see `readiness.ts`). `headline` is the bold stage/status label; `subline`
 *  folds the former standalone kicker into a single muted "<kicker> ·
 *  <detail>" line. */
function cardStatus(job: WorkUnitSummaryItem): {
  headline: string;
  subline: string;
} {
  if (job.readiness === "non_press") {
    return {
      headline: job.work_kind ?? "Non-press work",
      subline: "Non-press work",
    };
  }

  if (NOT_YET_IN_PRODUCTION.has(job.readiness)) {
    // waiting_reason (the CSR's own words) wins when present; otherwise
    // fall back to the readiness ladder's own label — same priority order
    // `boardReasonLine` already uses in PortfolioBoard.tsx.
    const headline =
      job.waiting_reason ??
      readinessLabel(job.readiness) ??
      "Not in production";
    const days = job.readiness_since ? daysSince(job.readiness_since) : null;
    return {
      headline,
      subline:
        days !== null
          ? `Not in production · ${days} ${plural("day", days)}`
          : "Not in production",
    };
  }

  // in_production
  const days = job.stage_updated_at ? daysSince(job.stage_updated_at) : null;
  return {
    headline: job.stage_label ?? "In production",
    subline:
      days === null
        ? "In production"
        : days === 0
          ? "In production · today"
          : `In production · ${days} ${plural("day", days)} ago`,
  };
}

/** The due pill: "N days late" (red), "Due today" / "Due tomorrow" / "Due
 *  in N days" (amber within the due-soon window, grey otherwise), or a
 *  neutral "Check due date" hint when the needed-by year looks wrong. Null
 *  (no pill) when there's no needed-by date at all. Calendar days
 *  throughout, matching the wording the design calls for — this is a
 *  display-only approximation of the backend's 3-*working*-day due-soon
 *  cutoff (`add_working_days`), not a re-implementation of it. */
function dueInfoPill(
  job: WorkUnitSummaryItem,
): { label: string; tone: "red" | "amber" | "neutral" } | null {
  if (!job.required_at) return null;
  if (job.needed_by_year_suspect) {
    return { label: "Check due date", tone: "neutral" };
  }

  const due = new Date(job.required_at);
  due.setHours(0, 0, 0, 0);
  const today = currentTime();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86_400_000);

  if (diffDays < 0) {
    const lateDays = Math.abs(diffDays);
    return {
      label: `${lateDays} ${plural("day", lateDays)} late`,
      tone: "red",
    };
  }
  if (diffDays === 0) return { label: "Due today", tone: "amber" };
  if (diffDays === 1) return { label: "Due tomorrow", tone: "amber" };
  const isDueSoon = diffDays <= 3;
  return {
    label: `Due in ${diffDays} days`,
    tone: isDueSoon ? "amber" : "neutral",
  };
}

/** Days elapsed since an ISO timestamp, floored to whole days — same shape
 *  as `PortfolioBoard.tsx`'s identical private helper. */
function daysSince(iso: string): number {
  return Math.floor(
    (currentTime().getTime() - new Date(iso).getTime()) / 86_400_000,
  );
}
